import React, { useCallback } from 'react';
import { useController } from 'react-hook-form';
import { TagFieldNames } from 'shared/constants/fields';
import { isNonArrayObject } from 'shared/utils/functions';
import styled from 'styled-components';
import MultiInput from './MultiInput';

/**
 * @type {import('./types').FieldTransformStrategy}
 */
const transform = {
  fromServer(value) {
    return typeof value === 'string' ? value : value?.map((t) => t.value);
  },
  toServer(value, { field }) {
    const { name } = field;
    const tags = (Array.isArray(value) ? value : value.split(','))
      .map((v) => (isNonArrayObject(v) && 'value' in v ? v.value : v))
      .map((str) => str.trim())
      .filter(Boolean);
    return { [name === TagFieldNames.TAGS ? 'tag_values' : name]: tags };
  },
  defaultValue(value) {
    /**
     * Tags come in like this ['one', 'two'] OR 'one,two',
     * while <MultiInput> expects [{ value: 'One' }, { value: 'Two' }].
     */
    const tags = typeof value === 'string' ? value.split(',') : value;
    return tags.map((t) => ({ value: t })).filter((t) => t.value.trim());
  },
};

const Tag = React.memo(function Tag({
  name,
  control,
  required,
  displayName,
  readOnly,
  setError,
}) {
  const {
    field: { onChange, value },
    fieldState: { error },
  } = useController({ name, control, rules: { required } });

  const validateInput = useCallback(
    (inputs = []) => {
      if (name === TagFieldNames.EMAIL_TO) {
        const regex = new RegExp(/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i);
        const invalidEmails = inputs.filter((email) => !email.match(regex));
        const validEmails = inputs.filter((email) => email.match(regex));

        if (invalidEmails.length) {
          if (!error) setError(`Invalid email address found`);
        } else {
          setError('');
        }

        onChange(validEmails);
      } else {
        onChange(inputs);
      }
    },
    [name, error, setError, onChange]
  );

  return (
    <div className="position-relative">
      <MultiInput
        placeholder={displayName}
        defaultValue={value}
        isDisabled={readOnly}
        onChange={validateInput}
      />
      {error && (
        <FieldError className="position-absolute fs-7 left-0 text-danger">
          {error.message}
        </FieldError>
      )}
    </div>
  );
});

const FieldError = styled.div`
  bottom: -1.35rem;
`;

Tag.transform = transform;
export default Tag;
